import { IconeHistorique } from '@/components/historique/icone-historique';
import { useTitrePage } from '@/lib/titre-page';
import { Alert, Button, Icon, buttonVariants } from '@pms-digital/design-system';
import type { Traduction } from '@rosetta/shared';
import { Link } from '@tanstack/react-router';
import { EncadreTraduction } from './encadre-traduction';
import { MessageTahiti } from './message-tahiti';

interface EcranEchecProps {
  traduction: Traduction;
  /** Opens the input screen filled with this translation (#5): nothing is sent before "traduire". */
  onRelancer: () => void;
}

/** What the page says when Tahiti gave no readable message. */
export const MESSAGE_ECHEC_SOBRE = "Le traitement s'est arrêté avant la fin.";

/** What the page says of a task that was created and never started (#13). */
export const MESSAGE_NON_DEMARREE = "Le traitement n'a jamais démarré : rien n'a été traduit.";

/**
 * A failed translation, at the address of its result (#13): the user stays where the
 * translation is, instead of being sent to an input screen that may hold another text (B4). The
 * title, "voir l'historique", the alert (the message of Tahiti as it is, or a sober sentence),
 * the box of the translation (title, three lines, noyaux), then "relancer", which opens the input
 * screen filled with this text and these noyaux (the flow of #5).
 */
export function EcranEchec({ traduction, onRelancer }: EcranEchecProps) {
  useTitrePage('Traduction non aboutie');
  const { messageErreur, nonDemarree } = traduction;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-h2 leading-[1.2]">
          <span className="font-light">Traduction</span>{' '}
          <span className="font-bold">non aboutie</span>
        </h1>
        <Link to="/historique" className={`${buttonVariants({ variant: 'ghost' })} no-underline`}>
          <Icon name="history" size={20} />
          voir l'historique
        </Link>
      </div>

      <div className="mt-8">
        <Alert tone="danger" title="La traduction n'a pas abouti.">
          {messageErreur === undefined ? (
            <p className="text-gray-700 text-small leading-[1.5]">
              {nonDemarree === true ? MESSAGE_NON_DEMARREE : MESSAGE_ECHEC_SOBRE}
            </p>
          ) : (
            <MessageTahiti message={messageErreur} />
          )}
        </Alert>
      </div>

      <EncadreTraduction traduction={traduction} />

      <div className="mt-6">
        <Button type="button" variant="secondary" onClick={onRelancer}>
          <IconeHistorique name="refresh" size={20} />
          relancer
        </Button>
      </div>
    </>
  );
}
